import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { CustomModal } from "../../../components/CustomModal";
import { MDBBtn, MDBCheckbox, MDBInput } from "mdb-react-ui-kit";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { Alert } from "../../../components/Alert";
import { TextInput } from "../../../components/core/inputs/TextInput";

export interface TransferModalProps {
    machineId: number;
    open: boolean;
    onShared: () => void;
    onClose: () => void;
}
export const TransferModal = ({machineId, open, onShared, onClose}: TransferModalProps) => {
    const [selectedId, setSelectedId] = useState(null);
    const [companies, setCompanies] = useState<any[]|null>(null);
    const [search, setSearch] = useState('');
    const [shareWithLink, setShareWithLink] = useState(false);
    const [shareLoading, setShareLoading] = useState(false);
    const [shareError, setShareError] = useState('');
    const [shareInvitation, setShareInvitation] = useState(null);
    const [shareSuccessMessage, setShareSuccessMessage] = useState('');
    const { t } = useTranslation();

    useEffect(() => {
        if (!open) return;
        setSearch('');
        setSelectedId(null);
        setShareWithLink(false);
        setShareLoading(false);
        setShareSuccessMessage('');
        setShareError('');
        setShareInvitation(null);
        setCompanies(null);
        API.GET(`/api/admmachines/${machineId}/transfer`)
            .then(v => {
                let c: any[] = [];
                if (v.parent) c.push({isParent: true, ...v.parent});
                if (v.subcompanies) c = c.concat(v.subcompanies);
                setCompanies(c);
            });
        
    }, [open, machineId]);

    const confirm = useCallback(() => {
        if (!shareWithLink){
            setShareLoading(true);
            API.POST(`/api/admmachines/${machineId}/transfer/c/${selectedId}`)
                .then(() => { setShareSuccessMessage(t('transfer_success')); onShared(); })
                .catch(err => { setShareError(err.message); })
                .finally(() => setShareLoading(false));
        }else{
            setShareLoading(true);
            API.POST(`/api/admmachines/${machineId}/transfer/link`)
                .then(({invitationId}) => { setShareInvitation(invitationId); })
                .catch(err => { setShareError(err.message); })
                .finally(() => setShareLoading(false));
        }

    }, [shareWithLink, machineId, selectedId]);

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={t('transfer')}
            footer={( (shareError || shareSuccessMessage || shareInvitation)
                ? <MDBBtn color="secondary" className="w-100" onClick={onClose}>{t('close')}</MDBBtn>
                : <>
                    <MDBBtn color="secondary" onClick={onClose}>{t('cancel')}</MDBBtn>
                    <MDBBtn color="danger" onClick={confirm} disabled={!shareWithLink && !selectedId}>{t('confirm')}</MDBBtn>
                </>
            )}
        >
            <div className="d-flex flex-column justify-content-center align-items-center w-100">
                {(companies == null || shareLoading) && <LoadingSpinner/>}
                {companies !== null && (
                    <div className="d-flex flex-column">
                        <Alert type="danger" message={shareError}/>
                        <Alert type="success" message={shareSuccessMessage}/>
                        {shareInvitation && (
                            <div className="d-flex flex-column align-items-center justify-content-center">
                                <span>{t('your_invitation_id')}:</span>
                                <h3 className="text-success" style={{letterSpacing: '10px', marginLeft: 10, fontWeight: 'bold'}}>{shareInvitation}</h3>
                            </div>
                        )}
                        <div className="d-flex flex-column w-100">
                            {(!shareError && !shareSuccessMessage && !shareInvitation) &&
                                <>
                                    <TextInput label={t('search')} value={search} onValueChange={setSearch}/>
                                    <MDBCheckbox label={t('share_with_invitation')} type="checkbox" name="shareWithLink" id="shareWithLink" checked={shareWithLink} onChange={ev => setShareWithLink(ev.currentTarget.checked)}/>
                                    <div className="d-flex flex-column overflow-auto px-1 pt-2 pb-2" style={{maxHeight: 500, minWidth: 300, userSelect: 'none'}}>
                                        {companies.map(l => (
                                            <div key={l.id} className={"flex-row align-items-center hover-shadow p-1"+(shareWithLink?' d-none':'')} style={{cursor: 'pointer', background: l.id == selectedId ? '#0000001a' :'', display: l.name.toLocaleUpperCase().indexOf(search.toLocaleUpperCase()) == '-1' ? 'none': 'flex'}} onClick={() => setSelectedId(l.id)}>
                                                <i className="fas fa-building text-primary"/>
                                                <span className="ms-1">{l.name}</span>
                                                {l.isParent && <span className="fst-italic">&nbsp;({t('parent')})</span>}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                )}
            </div>
            
        </CustomModal>
    );
}